// src/components/workspace/shell/CommsRail.js

import { MessagesSquare, Sparkles, Video } from "lucide-react";

/**
 * One rail for every way the two people talk to each other.
 *
 * Previously chat lived in a left drawer, the assistant in a right drawer, and
 * the video call in a card nested inside the page — so starting a session
 * pushed the actual work off screen. Putting them in a single docked rail means
 * the investigation stays visible while you talk.
 *
 * The panels are all kept mounted and toggled with CSS: switching tabs must not
 * tear down a live WebRTC session.
 */

export const COMMS_TAB = {
  CHAT: "chat",
  SESSION: "session",
  ASSISTANT: "assistant",
};

const TABS = [
  { id: COMMS_TAB.CHAT, label: "Chat", icon: MessagesSquare },
  { id: COMMS_TAB.SESSION, label: "Session", icon: Video },
  { id: COMMS_TAB.ASSISTANT, label: "Assistant", icon: Sparkles },
];

export default function CommsRail({
  activeTab,
  onTabChange,
  unreadCount = 0,
  sessionLive = false,
  panels = {},
}) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div
        role="tablist"
        aria-label="Communications"
        className="flex flex-shrink-0 gap-1 border-b border-slate-200 px-2 py-2"
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const showUnread = tab.id === COMMS_TAB.CHAT && unreadCount > 0 && !isActive;
          const showLive = tab.id === COMMS_TAB.SESSION && sessionLive;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onTabChange(tab.id)}
              className={`relative flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isActive
                  ? "bg-slate-800 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {tab.label}
              {showUnread && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-2xs font-bold text-white">
                  {Math.min(unreadCount, 9)}
                </span>
              )}
              {showLive && (
                <span
                  className="h-1.5 w-1.5 rounded-full bg-emerald-400"
                  aria-label="Session live"
                />
              )}
            </button>
          );
        })}
      </div>

      {TABS.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          aria-hidden={activeTab !== tab.id}
          className={`min-h-0 flex-1 ${
            activeTab === tab.id ? "flex flex-col" : "hidden"
          }`}
        >
          {panels[tab.id]}
        </div>
      ))}
    </div>
  );
}
